import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import useMercadoPago from '../../../hooks/useMercadoPago';
import { formatUnknownError } from '../../../utils/formatUnknownError';

const MERCADO_PAGO_PUBLIC_KEY =
  process.env.REACT_APP_MERCADO_PAGO_PUBLIC_KEY || 'APP_USR-59c72288-89f0-4bf3-9078-7fe364d96393';

/** Remove espaços invisíveis e normaliza casing do domínio (evita falha de validação no MP). */
export function normalizePayerEmail(raw: string | undefined | null): string {
  if (raw == null) return '';
  return raw.trim().replace(/\u200B/g, '').replace(/\s+/g, ' ').toLowerCase();
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidPayerEmailFormat(email: string): boolean {
  return email.length > 0 && EMAIL_REGEX.test(email);
}

export interface PaymentBrickProps {
  amount: number;
  preferenceId?: string;
  /** E-mail já informado no fluxo (ex.: inscrição). Pré-preenche Pix e pode ocultar o campo no Brick. */
  payerEmail?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  allowInstallments?: boolean; // Permitir parcelamento (desabilitado por enquanto)
  maxInstallments?: number;
  /** Restringe métodos exibidos no Brick (checkout híbrido). */
  brickMode?: 'all' | 'pix_only' | 'card_only';

  onCardTokenReceived?: (
    cardTokenId: string,
    paymentMethodId: string,
    issuerId: string | null,
    installmentsValue: number,
    payer?: {
      email?: string;
      first_name?: string;
      last_name?: string;
      identification?: {
        type: string;
        number: string;
      };
    },
    installmentTotalAmount?: number
  ) => Promise<void>;

}

const PaymentBrick: React.FC<PaymentBrickProps> = ({ 
  amount, 
  preferenceId,
  payerEmail,
  onSuccess, 
  onError, 
  onCardTokenReceived,
  allowInstallments = false, // Sempre false por enquanto - parcelamento desabilitado
  maxInstallments = 12,
  brickMode = 'all',
}) => {
  const { mercadoPago: mpInstance, loading: mpLoading } = useMercadoPago({
    publicKey: MERCADO_PAGO_PUBLIC_KEY,
    locale: 'pt-BR',
  });
  const brickControllerRef = useRef<{ unmount?: () => void } | null>(null);

  const normalizedPayerEmail = useMemo(
    () => (payerEmail === undefined || payerEmail === null ? undefined : normalizePayerEmail(String(payerEmail))),
    [payerEmail]
  );

  /** Quando o fluxo envia payerEmail, não monta o Brick até ter e-mail válido (evita Pix com campo vazio + “Dado obrigatório”). */
  const shouldBlockBrickUntilEmail =
    payerEmail !== undefined &&
    payerEmail !== null &&
    (!normalizedPayerEmail || !isValidPayerEmailFormat(normalizedPayerEmail));

  useEffect(() => {
    if (!mpInstance || !amount || shouldBlockBrickUntilEmail || mpLoading) {
      return;
    }

    const renderBrick = async () => {
      try {
        const container = document.getElementById('payment-brick-container');
        try {
          await brickControllerRef.current?.unmount?.();
        } catch {
          /* ignore */
        }
        brickControllerRef.current = null;
        if (container) {
          container.innerHTML = '';
        }

        const bricksBuilder = mpInstance.bricks();
        const initialization: Record<string, any> = { amount };
        if (preferenceId) {
          initialization.preferenceId = preferenceId;
        }
        if (normalizedPayerEmail && isValidPayerEmailFormat(normalizedPayerEmail)) {
          initialization.payer = { email: normalizedPayerEmail };
        }

        if (process.env.NODE_ENV === 'development' && initialization.payer?.email) {
          // eslint-disable-next-line no-console
          console.debug('[PaymentBrick] initialization.payer.email', initialization.payer.email);
        }

        const paymentMethods =
          brickMode === 'pix_only'
            ? {
                bankTransfer: 'all' as const,
                creditCard: 'none' as const,
                minInstallments: 1,
                maxInstallments: 1,
              }
            : brickMode === 'card_only'
              ? {
                  // Em algumas versões do Payment Brick, bankTransfer só aceita "pix"|"all".
                  // Para evitar 422, omitimos bankTransfer e deixamos somente cartão habilitado.
                  creditCard: 'all' as const,
                  minInstallments: 1,
                  maxInstallments: allowInstallments ? Math.max(1, Math.min(maxInstallments, 12)) : 1,
                }
              : {
                  bankTransfer: 'all' as const,
                  creditCard: 'all' as const,
                  minInstallments: 1,
                  maxInstallments: allowInstallments ? Math.max(1, Math.min(maxInstallments, 12)) : 1,
                };

        const controller = await bricksBuilder.create('payment', 'payment-brick-container', {
          initialization,
          customization: {
            paymentMethods,
          },
          callbacks: {
            onReady: () => {
              if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.debug('🧱 Payment Brick carregado.');
              }
            },
            onSubmit: async ({ formData }: any) => {
              try {
                const payer = formData.payer;
                const parsedTransactionAmount = Number(formData?.transaction_amount);
                const installmentTotalAmount =
                  Number.isFinite(parsedTransactionAmount) && parsedTransactionAmount > 0
                    ? parsedTransactionAmount
                    : amount;

                if (process.env.NODE_ENV === 'development') {
                  // eslint-disable-next-line no-console
                  console.debug('✅ Payment Brick formData:', formData);
                }

                if (onCardTokenReceived) {
                  const rawInstallments = Number(formData.installments || 1);
                  if (allowInstallments && rawInstallments > maxInstallments) {
                    const error = new Error(`Máximo permitido pela plataforma: ${maxInstallments}x.`);
                    if (onError) onError(error);
                    return;
                  }
                  const installmentsValue = allowInstallments === false ? 1 : Math.max(1, rawInstallments);
                  await onCardTokenReceived(
                    formData.token,
                    formData.payment_method_id,
                    formData.issuer_id || null,
                    installmentsValue,
                    payer,
                    installmentTotalAmount
                  );
                } else {
                  const response = await fetch('/api/payments/process-payment', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...formData,
                      payer,
                    }),
                  });
                  const result = await response.json();
                  if (result.success) {
                    if (onSuccess) onSuccess();
                  } else {
                    throw new Error(result.message);
                  }
                }
              } catch (error) {
                console.error('❌ Erro ao processar pagamento:', error);
                if (onError) onError(formatUnknownError(error, 'Erro ao processar pagamento.'));
              }
            },
            onError: (error: any) => {
              console.error('❌ Erro no Brick:', error);
              if (onError) onError(formatUnknownError(error, 'Erro no formulário de pagamento.'));
            },
          },
        });

        brickControllerRef.current = controller ?? null;
      } catch (error) {
        console.error('❌ Falha ao inicializar Payment Brick:', error);
        if (onError) onError(formatUnknownError(error, 'Não foi possível carregar o pagamento. Tente novamente.'));
      }
    };

    void renderBrick();

    return () => {
      try {
        brickControllerRef.current?.unmount?.();
      } catch {
        /* ignore */
      }
      brickControllerRef.current = null;
      const el = document.getElementById('payment-brick-container');
      if (el) el.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mpInstance,
    mpLoading,
    amount,
    preferenceId,
    normalizedPayerEmail,
    payerEmail,
    shouldBlockBrickUntilEmail,
    allowInstallments,
    maxInstallments,
    brickMode,
  ]);

  return (
    <Box>
      {mpLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {shouldBlockBrickUntilEmail && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          E-mail não disponível ou inválido. Volte ao passo anterior, confira o e-mail e salve antes de pagar.
        </Alert>
      )}
      <div id="payment-brick-container" style={{ display: shouldBlockBrickUntilEmail ? 'none' : undefined }} />
    </Box>
  );
};

export default PaymentBrick;
